import { useZones } from 'features/Responder/features/Zones';
import { centroid, normalizePoint } from 'features/Responder/features/Zones';
import React from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { PiCrosshair, PiX } from 'react-icons/pi';
import PlacesAutocompleteInput from './PlacesAutocompleteInput.jsx';

const StartLocationCard = ({
  startLocation,
  googleApiKey,
  usingCurrentStart,
  setUsingCurrentStart,
  setStartLocation,
  setAutoBuildPending,
  cardId,
}) => {
  let zonesWithMeta = [];
  try {
    const zonesCtx = useZones();
    zonesWithMeta = zonesCtx?.zonesWithMeta || zonesCtx?.zones || [];
  } catch (err) {
    zonesWithMeta = [];
  }

  const selectedZoneId = startLocation?.zoneId ?? startLocation?.geometry?.zoneId ?? '';
  const selectedZoneValue =
    selectedZoneId === null || selectedZoneId === undefined ? '' : `${selectedZoneId}`;

  const isZoneSelected = selectedZoneValue !== '';

  const handleClearZone = () => {
    if (!isZoneSelected) return;
    setStartLocation((prev) => ({
      ...prev,
      address: '',
      lat: '',
      lng: '',
      placeId: '',
      useCurrent: false,
      zoneId: null,
      geometry: null,
    }));
    setUsingCurrentStart(false);
    setAutoBuildPending(true);
  };

  const handleZoneChange = (event) => {
    const val = event.target.value;
    if (!val) {
      setStartLocation((prev) => ({
        ...prev,
        zoneId: null,
        geometry: null,
      }));
      setUsingCurrentStart(false);
      setAutoBuildPending(true);
      return;
    }

    const sel = zonesWithMeta.find((z) => `${z.id}` === `${val}`);
    if (!sel) return;

    const raw = sel.polygon ?? sel.geometry?.points ?? sel.geometry?.coord ?? sel._points ?? [];
    const points = Array.isArray(raw) ? raw.map((pt) => normalizePoint(pt)).filter(Boolean) : [];
    const newGeometry = {
      type: 'zone',
      points,
      coord: points,
      isZone: true,
      zoneId: sel.id,
    };
    const zoneLabel = sel.name || `Zone ${sel.id}`;
    const boundsCenter =
      sel?.bounds &&
      Number.isFinite(sel.bounds.north) &&
      Number.isFinite(sel.bounds.south) &&
      Number.isFinite(sel.bounds.east) &&
      Number.isFinite(sel.bounds.west)
        ? {
            lat: (sel.bounds.north + sel.bounds.south) / 2,
            lng: (sel.bounds.east + sel.bounds.west) / 2,
          }
        : null;
    const center = centroid(points) || boundsCenter || points[0] || null;

    setUsingCurrentStart(false);
    setStartLocation((prev) => ({
      ...prev,
      geometry: newGeometry,
      zoneId: sel.id,
      label: zoneLabel || prev.label,
      address: zoneLabel || prev.address,
      lat: center?.lat ?? prev.lat,
      lng: center?.lng ?? prev.lng,
      placeId: '',
      useCurrent: false,
    }));
    setAutoBuildPending(true);
  };

  return (
    <Card id={cardId} style={{ background: '#325277' }} className="shadow-sm">
      <Card.Body className="p-3">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div>
            <div className="fw-semibold">Start</div>
            <div className="text-muted small">Use current location or enter a start point.</div>
          </div>
          <Button
            variant={usingCurrentStart ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              if (!navigator.geolocation) return;
              setUsingCurrentStart(true);
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  const lat = position.coords.latitude;
                  const lng = position.coords.longitude;
                  setStartLocation((prev) => ({
                    ...prev,
                    lat,
                    lng,
                    placeId: '',
                    address: 'Current location',
                    useCurrent: true,
                    zoneId: null,
                    geometry: null,
                  }));
                  setAutoBuildPending(true);
                },
                () => setUsingCurrentStart(false),
              );
            }}
            title="Use current location"
          >
            <PiCrosshair size={24} />
          </Button>
        </div>

        <Form.Group className="mt-2">
          <Form.Label className="small text-muted mb-1">Address</Form.Label>
          <PlacesAutocompleteInput
            stop={startLocation}
            placeholder="Search address or place"
            apiKey={googleApiKey}
            disabled={isZoneSelected}
            onManualChange={(val) =>
              setStartLocation((prev) => ({
                ...prev,
                address: val,
                useCurrent: false,
              }))
            }
            onPlaceSelect={(payload) => {
              setStartLocation((prev) => ({
                ...prev,
                address: payload.address || prev.address,
                lat: payload.lat ?? prev.lat,
                lng: payload.lng ?? prev.lng,
                placeId: payload.placeId || prev.placeId,
                useCurrent: false,
                zoneId: null,
                geometry: null,
              }));
              setAutoBuildPending(true);
            }}
          />
        </Form.Group>

        <Form.Group className="mt-2">
          <Form.Label className="small text-muted mb-1">Zone</Form.Label>
          <div className="input-group input-group-sm">
            <Form.Select
              className="responder-input"
              value={selectedZoneValue}
              onChange={handleZoneChange}
            >
              <option value="">-- Select zone --</option>
              {zonesWithMeta.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name || `Zone ${z.id}`}
                </option>
              ))}
            </Form.Select>
            <Button
              variant="primary"
              onClick={handleClearZone}
              disabled={!isZoneSelected}
              title="Clear zone"
              aria-label="Clear zone"
            >
              <PiX size={16} />
            </Button>
          </div>
        </Form.Group>

        <Row className="mt-2 g-2">
          <Col>
            <Form.Label className="small text-muted mb-1">Latitude</Form.Label>
            <Form.Control
              className="border-0"
              size="sm"
              value={startLocation.lat}
              readOnly={isZoneSelected}
              onChange={(e) =>
                setStartLocation((prev) => ({
                  ...prev,
                  lat: e.target.value,
                  useCurrent: false,
                  zoneId: null,
                  geometry: null,
                }))
              }
              placeholder="e.g. 30.2672"
            />
          </Col>
          <Col>
            <Form.Label className="small text-muted mb-1">Longitude</Form.Label>
            <Form.Control
              className="border-0"
              size="sm"
              value={startLocation.lng}
              readOnly={isZoneSelected}
              onChange={(e) =>
                setStartLocation((prev) => ({
                  ...prev,
                  lng: e.target.value,
                  useCurrent: false,
                  zoneId: null,
                  geometry: null,
                }))
              }
              placeholder="-97.7431"
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default StartLocationCard;


